import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, CircleDot, GripVertical } from 'lucide-react';
import { useState } from 'react';

import { PriorityDot } from '@/components/tasks/PriorityDot';
import { useToast } from '@/components/ui/toast';
import { tasks as tasksApi } from '@/lib/api/endpoints';
import type { TaskStatus, TaskSummary } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT, type MessageKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** The three stages, left to right, in the order work moves through them. */
const STAGES: TaskStatus[] = ['OPEN', 'IN_PROGRESS', 'COMPLETED'];

const ICON: Record<string, typeof Circle> = {
  OPEN: Circle,
  IN_PROGRESS: CircleDot,
  COMPLETED: CheckCircle2,
};

/**
 * Tasks as columns you drag between.
 *
 * The stages are the ones the model already had — to do, in progress, done —
 * so a card dragged here and a button pressed on the task screen are the same
 * act, and the two can never disagree. That matters most at the right edge:
 * dropping into *done* completes the task through the ordinary path, so a
 * guarded task still refuses, one with unfinished steps still refuses, and the
 * clock still stops. A board that set the field directly would quietly be a
 * fourth way to close something, exempt from all of it.
 *
 * Dragging is native HTML5 rather than a library: a board is one drop target
 * per column, and a dependency buys nothing here. Every move is also a button,
 * because a board that only answers to dragging is a board half the people
 * cannot use.
 */
export function Board({
  projectId,
  onOpenTask,
}: {
  /** Narrows the board to one project. Omitted, it shows everything. */
  projectId?: string;
  onOpenTask: (taskId: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dragging, setDragging] = useState<TaskSummary | null>(null);
  const [over, setOver] = useState<TaskStatus | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['board', projectId ?? null],
    queryFn: () => tasksApi.board(projectId),
  });

  const move = useMutation({
    mutationFn: ({ task, to, order }: { task: TaskSummary; to: TaskStatus; order?: number }) =>
      moveTask(task, to, order),
    onSuccess: () => {
      for (const key of [['board'], ['tasks'], ['counts'], ['today']]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (failure) => toast.error(describeFailure(failure, 'error.taskUpdate')),
  });

  const columns = data?.columns ?? [];

  const drop = (to: TaskStatus) => {
    const task = dragging;
    setDragging(null);
    setOver(null);
    if (!task || task.status === to) return;
    // Land at the top of the column: the card you just moved is the one you
    // were thinking about, and hunting for it at the bottom is a small daily
    // annoyance that adds up.
    const first = columns.find((one) => one.status === to)?.tasks[0]?.sort_order;
    move.mutate({ task, to, order: first === undefined ? 0 : first - 1 });
  };

  if (isPending) return <p className="px-1 py-6 text-sm text-content-subtle">…</p>;

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {columns.map((column) => {
        const Icon = ICON[column.status] ?? Circle;
        const armed = over === column.status && dragging !== null && dragging.status !== column.status;
        return (
          <section
            key={column.status}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(column.status);
            }}
            onDragLeave={() => setOver((current) => (current === column.status ? null : current))}
            onDrop={() => drop(column.status)}
            className={cn(
              'rounded-xl bg-canvas p-2 transition-surface',
              armed && 'ring-2 ring-accent',
            )}
          >
            <h3 className="mb-2 flex items-center gap-1.5 px-1 text-xs font-medium uppercase tracking-wide text-content-subtle">
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {t(`board.stage.${column.status}` as MessageKey)}
              <span className="tabular-nums">{column.total}</span>
            </h3>

            <ul className="space-y-1.5">
              {column.tasks.map((task) => (
                <li key={task.id}>
                  <Card
                    task={task}
                    dragging={dragging?.id === task.id}
                    onDragStart={() => setDragging(task)}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    onOpen={() => onOpenTask(task.id)}
                    onMove={(to) => move.mutate({ task, to })}
                    showProject={projectId === undefined}
                  />
                </li>
              ))}
              {column.tasks.length === 0 ? (
                <li className="px-1 py-3 text-xs text-content-subtle">{t('board.empty')}</li>
              ) : null}
              {column.total > column.tasks.length ? (
                <li className="px-1 pt-1 text-[11px] text-content-subtle">
                  {t('board.more', { count: column.total - column.tasks.length })}
                </li>
              ) : null}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function Card({
  task,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
  onMove,
  showProject,
}: {
  task: TaskSummary;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onOpen: () => void;
  onMove: (to: TaskStatus) => void;
  /** Off inside a project, where every card would say the same name. */
  showProject: boolean;
}) {
  const t = useT();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'group rounded-lg bg-surface p-2 ring-1 ring-border transition-surface hover:ring-accent/40',
        dragging && 'opacity-40',
      )}
    >
      <div className="flex items-start gap-1.5">
        <GripVertical
          className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-grab text-content-subtle opacity-0 transition-surface group-hover:opacity-100 max-sm:opacity-100 touch:opacity-100"
          aria-hidden
        />
        <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
          <span
            className={cn(
              'line-clamp-2 text-sm text-content',
              task.status === 'COMPLETED' && 'text-content-muted line-through',
            )}
          >
            {task.title}
          </span>
        </button>
        <PriorityDot priority={task.priority} />
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-5 text-[11px] text-content-subtle">
        {showProject && task.project_name ? (
          <span className="truncate">{task.project_name}</span>
        ) : null}
        {task.subtask_total > 0 ? (
          <span className="tabular-nums">
            {task.subtask_done}/{task.subtask_total}
          </span>
        ) : null}
        {task.assignee ? <span className="truncate">{task.assignee.display_name}</span> : null}
      </div>

      <div className="mt-1.5 flex flex-wrap gap-1 pl-5">
        {STAGES.filter((stage) => stage !== task.status).map((stage) => (
          <button
            key={stage}
            type="button"
            onClick={() => onMove(stage)}
            title={t('board.moveTo', { stage: t(`board.stage.${stage}` as MessageKey) })}
            className="rounded px-1.5 py-0.5 text-[11px] text-content-muted opacity-0 transition-surface hover:bg-surface-hover hover:text-content focus:opacity-100 group-hover:opacity-100 max-sm:min-h-8 max-sm:opacity-100 touch:min-h-8 touch:opacity-100"
          >
            → {t(`board.stage.${stage}` as MessageKey)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * One drop, expressed in the app's own verbs.
 *
 * Completing and reopening are endpoints, not fields, because both carry rules
 * — a guard to clear, steps to finish, a running clock to stop, an activity
 * entry to write. The board goes through them like every other screen.
 */
async function moveTask(task: TaskSummary, to: TaskStatus, order?: number) {
  if (to === 'COMPLETED') return tasksApi.complete(task.id);
  if (task.status === 'COMPLETED') await tasksApi.reopen(task.id);
  return tasksApi.update(task.id, {
    status: to as Exclude<TaskStatus, 'COMPLETED'>,
    ...(order === undefined ? {} : { sort_order: order }),
  });
}
